import './prismSetup';
import './wdyr';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import * as Sentry from '@sentry/react';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { Logger } from 'src/utils/Logger';
import Config from 'src/common/Config';
import { apiOriginPattern } from 'src/common/apiOriginPattern';
import { ApiError } from 'src/error/ApiError';
import { UnauthenticatedError } from 'src/common/utils/api/fetchWrapper';
import { msalInstance } from 'src/data-capture/file-management/drop-zone/msalInstance';

msalInstance.initialize().then(() =>
  msalInstance
    .handleRedirectPromise()
    .then(response => {
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
      }
    })
    .catch(error => {
      // A failed sign-in returning here (e.g. consent still not granted) would otherwise
      // be swallowed, leaving the user on a page that just reloaded with no explanation.
      Logger.error('MSAL redirect sign-in failed', error);
    }),
);

function shouldDropError(originalError: unknown): boolean {
  if (originalError instanceof ApiError || originalError instanceof UnauthenticatedError) {
    return true;
  }

  // Browser extension noise — no stacktrace, not from application code
  if (
    typeof originalError === 'string' &&
    originalError.includes('Object Not Found Matching Id:')
  ) {
    return true;
  }

  if (
    originalError instanceof Error &&
    originalError.message.includes('Invalid call to runtime.sendMessage()')
  ) {
    return true;
  }

  return false;
}

Sentry.init({
  dsn: Config.getSentryDsn(),
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost:8000', apiOriginPattern()],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment: Config.getObservabilityEnvironment(),
  enabled: Config.isProd() || Config.isStaging(),
  release: Config.getReleaseVersion(),
  beforeSend(event, hint) {
    // Prevent re-reporting of ApiErrors and UnauthenticatedErrors by global error handlers
    // (they were already handled by dataProvider)
    if (shouldDropError(hint.originalException)) {
      return null;
    }

    return event;
  },
});

Mixpanel.initialise();

document.addEventListener('wheel', function () {
  // @ts-expect-error it thinks there's no type
  if (document?.activeElement?.type === 'number') {
    // @ts-expect-error it thinks there's no blur
    document.activeElement.blur();
  }
});

window.addEventListener('error', event => {
  Logger.error('Global error:', event.error);
});

window.addEventListener('unhandledrejection', event => {
  Logger.error('Unhandled promise rejection:', event.reason);
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
